import React, { useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { authUser, edgeInset } from '../config';
import { useQuestChain } from '../hooks/useQuestChain';

// StoryPanel — an ADMIN launcher for THE COME UP.
//
// Honest about what it is: a test harness, not the product. The treatment is
// explicit that every system function has a diegetic home — Lyra rings you, a
// stylist waves you over, a driver asks where you're headed — and a floating
// list of acts is exactly the floating panel it forbids.
//
// TWO of those homes now exist, on the phone: Act 0 rings you and Act 2 is a
// ride you hail. Those are the player's way in; this panel jumps straight to
// any act out of order, which is what testing needs and what a story cannot
// allow. Act 1 still has no boutique and Acts 4–5 have no scenes, so for those
// this is still the only door.
//
// Shows live quest state from /api/quests so you can see the chain gate itself:
// acts unlock as their prerequisites complete, exactly as a player would meet
// them.
// LISTED IN THE INTRO'S ORDER (Build_Game_Intro §2, migration 277): the ride
// comes BEFORE the salon now — Spor1 extracts genre so Sassy styles for a
// sound she already knows. Scene keys stay historical; the order is the truth.
const ACTS = [
  { quest: 'come_up_arrival', label: 'Act 0 · Arrival', scene: 'act0_lyra_call', invite: 'Lyra', note: 'phone: she rings on arrival' },
  { quest: 'come_up_the_ride', label: 'The Ride · Spor1', scene: 'RIDE', invite: 'The ride', note: 'kerb pickup, or hail in Rides' },
  { quest: 'come_up_the_look', label: 'The Salon', scene: 'act1_stylist', invite: 'The salon', note: 'after the ride now' },
  { quest: 'come_up_the_session', label: 'The Session', scene: 'act3_session', invite: 'The session', note: 'walk into the Studio' },
  { quest: 'come_up_the_drop', label: 'The Drop', scene: null, note: 'Walk out — the premiere' },
  { quest: 'come_up_open_city', label: 'The Handoff', scene: 'act5_handoff', note: 'she calls — the ending' },
];

// The intro ride is Spor1's, WITH the car (camera tour + reveals). The flavour
// rides run scene-only — open-city material, still testable.
const RIDES = [
  { key: 'SPOR1', label: "Spor1's ride", flavour: 'the intro · car + tour + reveals' },
  { key: 'act2_ride_spor1', label: 'Spor1 (scene only)', flavour: 'interview variant, no car' },
  { key: 'act2_ride_spor1_return', label: 'Spor1 · homecoming', flavour: 'recognition variant, no car' },
  { key: 'act2_ride_bus', label: 'The 47 bus', flavour: 'open city · community' },
  { key: 'act2_ride_hyper', label: 'Hyper', flavour: 'open city · indifferent' },
  { key: 'act2_ride_limo', label: 'The limo', flavour: 'open city · industry' },
];

const wrap = {
  position: 'fixed', left: edgeInset(16, 'left'), top: '84px', zIndex: 68,
  width: 'min(320px, calc(100vw - 32px))', maxHeight: '70vh', overflowY: 'auto',
  background: 'rgba(14,9,6,0.94)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 14,
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', color: '#ffe9c4',
  fontFamily: 'system-ui, sans-serif', padding: 12, pointerEvents: 'auto',
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
};
const row = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 0', borderBottom: '1px solid rgba(255,178,94,0.14)' };
const btn = { padding: '6px 11px', borderRadius: 8, border: '1px solid rgba(255,178,94,0.45)', background: 'rgba(255,178,94,0.12)', color: '#ffe9c4', font: '700 12px system-ui', cursor: 'pointer', whiteSpace: 'nowrap' };
const dim = { opacity: 0.55, font: '600 11px system-ui' };

const STATUS_COLOR = { completed: '#9dff7d', active: '#ffd27d', available: 'rgba(255,233,196,0.75)' };

export default function StoryPanel() {
  const tool = useWorldStore((s) => s.adminTool);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const openScene = useWorldStore((s) => s.openScene);
  const ring = useWorldStore((s) => s.ring);
  const [pickRide, setPickRide] = useState(false);

  const { isAdmin } = authUser();
  const open = tool === 'story' && isAdmin;
  const { quests, error: err } = useQuestChain(myAvatarId, { enabled: open });

  if (!open) return null;

  // A COMPLETED non-repeatable quest is filtered out of /api/quests entirely,
  // so an act that vanishes from this list has been finished — not lost.
  const statusOf = (slug) => quests.find((q) => q.slug === slug)?.status || 'done / locked';

  // SceneDock owns the conversation; this only says which scene to run. Scenes
  // launched here carry `room: null` — they're being started out of order for
  // testing, so walking through a door must not cancel them.
  const launch = (sceneKey, invite, quest) => {
    useWorldStore.setState({ adminTool: null });
    openScene({ key: sceneKey, title: invite, quest: quest || null, room: null });
  };

  // Act 0 is a CALL — run it the way the player meets it, ringing on the phone,
  // rather than dropping its transcript into the room dock.
  const launchCall = (sceneKey, from, quest) => {
    useWorldStore.setState({ adminTool: null });
    ring({ sceneKey, quest, from, role: 'Manager', giverRef: 'lyra' });
    useWorldStore.setState({ phoneOpen: true });
  };

  return (
    <div style={wrap}>
      <div style={{ font: '800 13px system-ui', letterSpacing: 0.4, marginBottom: 8 }}>THE COME UP</div>
      {!myAvatarId && <div style={{ ...dim, color: '#ffd27d', marginBottom: 8 }}>Pick which artist you're here as first.</div>}
      {err && <div style={{ ...dim, color: '#ff8a9c' }}>{err}</div>}

      {ACTS.map((a) => {
        const st = statusOf(a.quest);
        return (
          <div key={a.quest} style={row}>
            <div style={{ minWidth: 0 }}>
              <div style={{ font: '700 12.5px system-ui' }}>{a.label}</div>
              <div style={{ ...dim, color: STATUS_COLOR[st] || 'rgba(255,233,196,0.5)' }}>
                {st}{a.note ? ` · ${a.note}` : ''}
              </div>
            </div>
            {a.scene === 'RIDE' ? (
              <button style={btn} onClick={() => setPickRide((v) => !v)} disabled={!myAvatarId}>Ride ▾</button>
            ) : a.scene === 'act0_lyra_call' ? (
              <button style={btn} onClick={() => launchCall(a.scene, a.invite, a.quest)} disabled={!myAvatarId}>Ring</button>
            ) : a.scene === 'act5_handoff' ? (
              // The handoff is HER call — no giver to report, the objective is
              // the autonomy ledger, evaluated server-side on sync.
              <button style={btn} onClick={() => {
                useWorldStore.setState({ adminTool: null });
                ring({ sceneKey: a.scene, quest: a.quest, from: 'Her', role: 'Your artist', giverRef: null });
                useWorldStore.setState({ phoneOpen: true });
              }} disabled={!myAvatarId}>Ring</button>
            ) : a.scene ? (
              <button style={btn} onClick={() => launch(a.scene, a.invite, a.quest)} disabled={!myAvatarId}>Run</button>
            ) : null}
          </div>
        );
      })}

      {pickRide && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,178,94,0.2)' }}>
          <div style={{ ...dim, marginBottom: 6 }}>Same destination, different interviewer:</div>
          {RIDES.map((r) => (
            <div key={r.key} style={row}>
              <div>
                <div style={{ font: '700 12.5px system-ui' }}>{r.label}</div>
                <div style={dim}>{r.flavour}</div>
              </div>
              <button style={btn} onClick={async () => {
                setPickRide(false);
                if (r.key !== 'SPOR1') { launch(r.key, r.label, 'come_up_the_ride'); return; }
                // The full experience: server picks the variant, the car takes
                // the camera — same path the kerb pickup uses.
                let key = 'act2_ride_spor1';
                let title = "Spor1's ride";
                try {
                  const { api } = await import('../services/api');
                  const d = await api.dialogueRide(myAvatarId);
                  if (d?.scene) { key = d.scene; title = d.title || title; }
                } catch { /* default stands */ }
                useWorldStore.setState({ adminTool: null });
                const st = useWorldStore.getState();
                st.openScene({ key, title, quest: 'come_up_the_ride', room: null });
                st.startRide({ sceneKey: key, pace: 'touring' });
              }}>Run</button>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...dim, marginTop: 10, lineHeight: 1.5 }}>
        Admin test harness — it jumps acts out of order, which the story doesn't.
        The player's way in is the phone (Lyra rings; rides are hailed) and the
        Studio door. Acts gate on prerequisites, so an act you can't reach means
        the one before it isn't finished — that's the chain working.
      </div>
    </div>
  );
}
